import {
  SkyButton,
  SkyCard,
  SkyInput,
  SkyMessageBar,
  SkySwitch,
  SkyTabList,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useMemo, useState, type ReactElement } from "react";
import type { PlatformSettingKey, PlatformSettingView } from "@shared/types/settings";
import { TopbarActionsPortal } from "@/components/navigation/TopbarActionsPortal";
import { EgressTab } from "@/components/platform/EgressTab";
import { EmailDeliveryCard } from "@/components/platform/EmailDeliveryCard";
import { L1RecipientsCard } from "@/components/platform/L1RecipientsCard";
import { SendL1TeamsTestDialog } from "@/components/platform/SendL1TeamsTestDialog";
import { SecurityDeliveriesCard } from "@/components/platform/SecurityDeliveriesCard";
import { CanaryCard } from "@/components/platform/CanaryCard";
import { MfaSettingsCard } from "@/components/platform/MfaSettingsCard";
import { ReauthDialog } from "@/components/settings/ReauthDialog";
import { useReauthRetry } from "@/hooks/use-group-access";
import { isStepUpRequired } from "@/lib/auth/mfaStepUp";
import { PlatformHealthTab } from "@/components/platform/PlatformHealthTab";
import { ReplaceSecretDialog } from "@/components/platform/ReplaceSecretDialog";
import { ErrorState, GridSkeleton } from "@/components/platform/ScreenState";
import {
  KNOWN_FEATURE_FLAGS,
  NUMERIC_SETTINGS,
  PREVIEW_SETTINGS_FLAG,
  settingFallback,
  settingLabel,
} from "@/components/platform/settingsCatalog";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { reportFailureShown } from "@/lib/feedback/failure";
import { useApiErrorNotice } from "@/lib/feedback/useApiErrorNotice";
import {
  settingByKey,
  usePlatformSettings,
  useUpdatePlatformSetting,
} from "@/hooks/use-platform-settings";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { ResetIcon } from "@/lib/icons";
import { FORM_MAX_WIDTH } from "@/lib/layout";
import { MCP_FLAG } from "@shared/mcp/constants";
import { formatInstant } from "@/lib/time/displayTime";
import { cancelledByWording } from "@/components/runs/runFormatting";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * Platform settings (FR-71, FR-72, SEC-2, canvas 14).
 *
 * Four tabs, and they differ in kind rather than only in content: General and
 * Feature flags are WRITABLE, Egress is COMPUTED and Health is OBSERVED. The
 * tab strip is the only place those three live together, so each tab says which
 * it is before the reader looks for a save button that is not there.
 *
 * The catalog of settings is closed on the server, so this screen renders what
 * the route returns rather than a hard-coded list: a key added to
 * `PLATFORM_SETTINGS` appears here, and one removed stops appearing instead of
 * writing to a key that no longer validates.
 *
 * Controls that are stored but read by nothing yet (audit retention and the
 * Transformations, Entra group mapping and Usage report flags) render only when
 * the saved `settings.preview` flag is on; off, the screen says nothing about
 * them. The server stores their keys either way (product gaps row 20).
 *
 * Saving writes one key per request, because that is what the route takes: a
 * PUT names one key, and each one writes its own audit row. There is no
 * all-or-nothing save to pretend otherwise — the button reports what was saved
 * and what was not.
 */

type TabValue = "general" | "flags" | "egress" | "health";

const TABS = [
  { value: "general", label: "General" },
  { value: "flags", label: "Feature flags" },
  { value: "egress", label: "Egress allow-list" },
  { value: "health", label: "Health" },
];

const TEAMS_WEBHOOK_KEY: PlatformSettingKey = "alerts.teams_webhook";
const FEATURE_FLAGS_KEY: PlatformSettingKey = "features.flags";

/** The stored value for a key, or the catalog's fallback when it was never written. */
function valueOf(settings: PlatformSettingView[] | undefined, key: PlatformSettingKey): unknown {
  const view = settingByKey(settings, key);
  return view?.value ?? settingFallback(key);
}

function flagsOf(settings: PlatformSettingView[] | undefined): Record<string, boolean> {
  const raw = valueOf(settings, FEATURE_FLAGS_KEY);
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return {};
  return Object.fromEntries(
    Object.entries(raw as Record<string, unknown>).filter(
      (entry): entry is [string, boolean] => typeof entry[1] === "boolean",
    ),
  );
}

/** " Last changed <when> by <who>." after the webhook was set; nothing before. */
function webhookLastChanged(teamsWebhook: PlatformSettingView | undefined, zone: string): string {
  if (!teamsWebhook?.updatedAt) return "";
  // #2398: the server's label as it reads mid-sentence ("by the system", "by a removed user").
  const by = teamsWebhook.updatedByName
    ? ` by ${cancelledByWording(teamsWebhook.updatedByName)}`
    : "";
  return ` Last changed ${formatInstant(teamsWebhook.updatedAt, zone, "short", { named: true })}${by}.`;
}

/** #2165: names the instance channels, so the two Teams settings are told apart. */
const L1_TEAMS_CHANNEL_DIFFERENCE =
  "This is Skypoint's L1 channel for every failure on the deployment. Each instance can also post to its own team's channel, set in that instance's Settings.";

/**
 * The L1 Teams channel: where its webhook is stored, Replace (or Set), when it changed and, once
 * a webhook is stored, "Send a test" (#2165).
 */
function TeamsWebhookCard({
  teamsWebhook,
  zone,
  onReplace,
}: {
  teamsWebhook: PlatformSettingView | undefined;
  zone: string;
  onReplace: () => void;
}) {
  const [testOpen, setTestOpen] = useState(false);
  return (
    <SkyCard appearance="outline" testId="l1-teams-card">
      <div className="flex flex-col gap-3 p-1">
        <div>
          <SkyText as="h2" size={400} weight="semibold" block>
            L1 Teams channel
          </SkyText>
          <SkyText size={200} block testId="l1-teams-difference">
            {L1_TEAMS_CHANNEL_DIFFERENCE}
          </SkyText>
          <SkyText size={200} color="muted" block>
            That includes a failed run, a rejected or expired credential and a failed data-health
            check. Scheduler alerts (stale scans, queue backlog) are not sent yet.
          </SkyText>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <SkyText size={300} weight="medium">
            {settingLabel(TEAMS_WEBHOOK_KEY)}
          </SkyText>
          {teamsWebhook?.secret ? (
            <SkyText size={200} color="muted" font="monospace" testId="teams-webhook-pointer">
              Stored · {teamsWebhook.secret.vaultName} / {teamsWebhook.secret.secretName}
            </SkyText>
          ) : (
            <SkyText size={200} color="muted" testId="teams-webhook-pointer">
              Not configured
            </SkyText>
          )}
          <SkyButton
            appearance="secondary"
            size="small"
            onClick={onReplace}
            testId="replace-teams-webhook"
          >
            {teamsWebhook?.secret ? "Replace" : "Set webhook"}
          </SkyButton>
          {teamsWebhook?.secret ? (
            <SkyButton
              appearance="secondary"
              size="small"
              onClick={() => setTestOpen(true)}
              testId="send-l1-teams-test"
            >
              Send a test
            </SkyButton>
          ) : null}
        </div>

        <SkyText size={200} color="muted" block>
          The URL is written to Key Vault first, and only then does the deployment point at the new
          version. It is never returned to this screen.
          {webhookLastChanged(teamsWebhook, zone)}
        </SkyText>
      </div>
      {testOpen ? <SendL1TeamsTestDialog open={testOpen} onOpenChange={setTestOpen} /> : null}
    </SkyCard>
  );
}

/**
 * The General or Feature flags tab once the settings are read; loading and the read's failure
 * first. Called, not rendered as a component, so switching tabs patches the same elements.
 */
function settingsTabBody({
  settings,
  tab,
  general,
  flags,
}: {
  settings: ReturnType<typeof usePlatformSettings>;
  tab: TabValue;
  general: () => ReactElement;
  flags: () => ReactElement;
}): ReactElement {
  if (settings.isPending) {
    return <GridSkeleton rows={4} label="Loading platform settings…" testId="settings-loading" />;
  }
  if (settings.isError) {
    return (
      <ErrorState
        title="Platform settings did not load"
        error={settings.error}
        onRetry={() => void settings.refetch()}
        testId="settings-error"
      />
    );
  }
  return tab === "general" ? general() : flags();
}

function generalTab({
  preview,
  numericSettings,
  draftValue,
  fieldErrors,
  onDraftChange,
  webhookCard,
}: {
  preview: boolean;
  numericSettings: typeof NUMERIC_SETTINGS;
  draftValue: (key: PlatformSettingKey) => string;
  fieldErrors: Record<string, string>;
  onDraftChange: (key: PlatformSettingKey, value: string) => void;
  webhookCard: ReactElement;
}): ReactElement {
  return (
    <div className={`flex flex-col gap-4 ${FORM_MAX_WIDTH}`} data-testid="general-tab">
      <SkyCard appearance="outline">
        <div className="flex flex-col gap-4 p-1">
          <div>
            <SkyText as="h2" size={400} weight="semibold" block>
              {preview ? "Retention and limits" : "Limits"}
            </SkyText>
            <SkyText size={200} color="muted" block>
              Each of these is one key in the closed platform catalog, and each save writes its own
              audit row.
            </SkyText>
          </div>

          {numericSettings.map((descriptor) => (
            <SkyInput
              key={descriptor.key}
              label={`${settingLabel(descriptor.key)}`}
              type="number"
              value={draftValue(descriptor.key)}
              onChange={(event) => onDraftChange(descriptor.key, event.target.value)}
              suffix={descriptor.unit}
              hint={descriptor.help}
              validationState={fieldErrors[descriptor.key] ? "error" : "none"}
              validationMessage={fieldErrors[descriptor.key]}
              testId={`setting-${descriptor.key}`}
            />
          ))}

          {preview ? (
            <SkyMessageBar
              intent="info"
              layout="multiline"
              title="Partitions drop only after the export is verified"
              message="The monthly export job writes to immutable storage and checks the month is readable there before the partition is dropped. Shortening retention here never shortens that order."
            />
          ) : null}
        </div>
      </SkyCard>

      {webhookCard}

      {/* Admin MFA (design 4.3): the mode, its own save, the floor and the providers. */}
      <MfaSettingsCard />

      {/* contract-notif R9: the L1 addresses (their own route and save) and the read-only
          Email delivery card with its test email. */}
      <L1RecipientsCard />
      <EmailDeliveryCard />
      <SecurityDeliveriesCard />
      {/* #826: the daily canary's newest verdicts (GET /platform/canary). */}
      <CanaryCard />
    </div>
  );
}

function flagsTab({
  someFixed,
  flagRows,
  flagValue,
  onFlagChange,
  mcpOriginMissing,
}: {
  /** `PUBLIC_APP_URL` is unset: the AI apps flag can be On and still do nothing (B-47). */
  mcpOriginMissing: boolean;
  /** #2501: the deployment policy fixes at least one flag, so its switch is not shown. */
  someFixed: boolean;
  flagRows: Array<{ key: string; label: string; description: string }>;
  flagValue: (key: string) => boolean;
  onFlagChange: (key: string, checked: boolean) => void;
}): ReactElement {
  return (
    <div className={`flex flex-col gap-4 ${FORM_MAX_WIDTH}`} data-testid="flags-tab">
      <SkyCard appearance="outline">
        <div className="flex flex-col gap-4 p-1">
          <div>
            <SkyText as="h2" size={400} weight="semibold" block>
              Feature flags
            </SkyText>
            <SkyText size={200} color="muted" block>
              Flags turn whole screens and routes on and off across the deployment. A flag that is
              off hides the nav entry and refuses the route on the server. Hiding is never the
              control.
            </SkyText>
          </div>

          {flagRows.length === 0 ? (
            <SkyText size={300} block testId="flags-all-fixed">
              Skypoint set every feature of this deployment. A change goes through Skypoint.
            </SkyText>
          ) : null}
          {flagRows.length > 0 && someFixed ? (
            <SkyText size={200} color="muted" block testId="flags-some-fixed">
              Some features are set for this deployment by Skypoint.
            </SkyText>
          ) : null}

          {flagRows.map((flag) => (
            <div key={flag.key} className="flex flex-col gap-2">
              <SkySwitch
                label={flag.label}
                description={flag.description}
                checked={flagValue(flag.key)}
                onCheckedChange={(checked) => onFlagChange(flag.key, checked)}
                testId={`flag-${flag.key}`}
              />
              {flag.key === MCP_FLAG && mcpOriginMissing ? (
                <SkyMessageBar
                  intent="warning"
                  layout="multiline"
                  testId="flag-mcp-origin-missing"
                  title="This deployment has no public address set"
                  message="PUBLIC_APP_URL is not set, so the AI app endpoints answer 404 and the AI apps screens stay hidden even with this on. Set it on the web app and restart it."
                />
              ) : null}
            </div>
          ))}
        </div>
      </SkyCard>
    </div>
  );
}

export default function AdminPlatformSettingsPage() {
  const zone = useDisplayTz();
  const notice = useNotice();
  // Save changes sits in the page header, so its failure lands in the notice right under it.
  const showError = useApiErrorNotice();
  const settings = usePlatformSettings();
  const meFeatures = useTenantInstance().me?.features;
  const updateSetting = useUpdatePlatformSetting();
  // Admin MFA (design 4.2): a setting write is a privileged route. A refusal holds the save in the
  // re-authentication dialog and replays it once identity is confirmed.
  const settingsRetry = useReauthRetry();

  const [tab, setTab] = useState<TabValue>("general");
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [flagDrafts, setFlagDrafts] = useState<Record<string, boolean>>({});
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const storedFlags = useMemo(() => flagsOf(settings.data), [settings.data]);
  // The SAVED value decides, so a switch flipped and not yet saved shows nothing new.
  const preview = storedFlags[PREVIEW_SETTINGS_FLAG] === true;

  const numericSettings = useMemo(
    () => NUMERIC_SETTINGS.filter((descriptor) => preview || !descriptor.preview),
    [preview],
  );

  // Every flag the deployment knows about: the described ones first, then any
  // key the stored document carries that this release has never heard of. A
  // flag that is on and invisible is the worst of the three states, except for
  // a described preview flag, which `settings.preview` shows on purpose.
  // #2501: a flag the deployment policy fixes gets no switch at all (the server names them).
  const fixedFlags = useMemo(
    () => new Set(settingByKey(settings.data, FEATURE_FLAGS_KEY)?.fixedByDeployment ?? []),
    [settings.data],
  );
  const flagRows = useMemo(() => {
    const known = KNOWN_FEATURE_FLAGS.filter(
      (flag) => (preview || !flag.preview) && !fixedFlags.has(flag.key),
    ).map((flag) => ({
      ...flag,
      isKnown: true,
    }));
    const extra = Object.keys(storedFlags)
      .filter((key) => !fixedFlags.has(key))
      .filter((key) => !KNOWN_FEATURE_FLAGS.some((flag) => flag.key === key))
      .map((key) => ({
        key,
        label: key,
        description: "Set on this deployment but not described by this release.",
        isKnown: false,
      }));
    return [...known, ...extra];
  }, [storedFlags, preview, fixedFlags]);

  const dirtyNumericKeys = useMemo(
    () =>
      numericSettings
        .filter((descriptor) => {
          const draft = drafts[descriptor.key];
          if (draft === undefined) return false;
          return draft !== String(valueOf(settings.data, descriptor.key) ?? "");
        })
        .map((descriptor) => descriptor.key),
    [drafts, settings.data, numericSettings],
  );

  const flagsDirty = Object.entries(flagDrafts).some(
    ([key, value]) => (storedFlags[key] ?? false) !== value,
  );

  const isDirty = dirtyNumericKeys.length > 0 || flagsDirty;

  function draftValue(key: PlatformSettingKey): string {
    return drafts[key] ?? String(valueOf(settings.data, key) ?? "");
  }

  function flagValue(key: string): boolean {
    return flagDrafts[key] ?? storedFlags[key] ?? false;
  }

  function discard() {
    setDrafts({});
    setFlagDrafts({});
    setFieldErrors({});
  }

  async function save() {
    const errors: Record<string, string> = {};
    const writes: Array<{ key: string; value: unknown }> = [];

    for (const descriptor of numericSettings) {
      if (!dirtyNumericKeys.includes(descriptor.key)) continue;
      const raw = draftValue(descriptor.key).trim();
      const parsed = Number(raw);
      if (raw === "" || !Number.isInteger(parsed)) {
        errors[descriptor.key] = "Enter a whole number.";
        continue;
      }
      if (parsed < descriptor.min) {
        errors[descriptor.key] =
          descriptor.floorReason ??
          `The minimum is ${descriptor.min} ${descriptor.unit}. Enter ${descriptor.min} or more.`;
        continue;
      }
      if (parsed > descriptor.max) {
        errors[descriptor.key] = `The maximum is ${descriptor.max} ${descriptor.unit}.`;
        continue;
      }
      writes.push({ key: descriptor.key, value: parsed });
    }

    if (flagsDirty) {
      writes.push({ key: FEATURE_FLAGS_KEY, value: { ...storedFlags, ...flagDrafts } });
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0 || writes.length === 0) return;

    const report = (saved: number) => {
      if (saved > 0) {
        notice.success(saved === 1 ? "Setting saved" : `${saved} settings saved`);
        setDrafts({});
        setFlagDrafts({});
      }
    };
    const writeAll = async (): Promise<number> => {
      let saved = 0;
      for (const write of writes) {
        try {
          await updateSetting.mutateAsync({ key: write.key, value: write.value });
          saved += 1;
        } catch (error) {
          // Refused before anything was written: hold the whole save for after the sign-in.
          if (saved === 0 && isStepUpRequired(error)) throw error;
          showError(error, "That setting was not saved.");
          break;
        }
      }
      return saved;
    };
    const saved = await settingsRetry.run(writeAll, report);
    if (saved !== null) report(saved);
  }

  const teamsWebhook = settingByKey(settings.data, TEAMS_WEBHOOK_KEY);

  const writable = tab === "general" || tab === "flags";

  return (
    <div className="flex flex-col gap-4" data-testid="platform-settings-page">
      {writable ? (
        <TopbarActionsPortal>
          <SkyButton
            appearance="subtle"
            icon={ResetIcon}
            disabled={!isDirty || updateSetting.isPending}
            onClick={discard}
            testId="discard-settings"
          >
            Discard
          </SkyButton>
          <SkyButton
            appearance="primary"
            disabled={!isDirty}
            loading={updateSetting.isPending}
            onClick={() => void save()}
            testId="save-settings"
          >
            Save changes
          </SkyButton>
        </TopbarActionsPortal>
      ) : null}

      <SkyTabList
        tabs={TABS}
        selectedValue={tab}
        onTabSelect={(value) => setTab(value as TabValue)}
        testId="platform-settings-tabs"
      />

      {writable
        ? settingsTabBody({
            settings,
            general: () =>
              generalTab({
                preview,
                numericSettings,
                draftValue,
                fieldErrors,
                onDraftChange: (key, value) =>
                  setDrafts((current) => ({ ...current, [key]: value })),
                webhookCard: (
                  <TeamsWebhookCard
                    teamsWebhook={teamsWebhook}
                    zone={zone}
                    onReplace={() => {
                      updateSetting.reset();
                      setReplaceOpen(true);
                    }}
                  />
                ),
              }),
            flags: () =>
              flagsTab({
                mcpOriginMissing: meFeatures?.mcpOriginSet === false,
                someFixed: fixedFlags.size > 0,
                flagRows,
                flagValue,
                onFlagChange: (key, checked) =>
                  setFlagDrafts((current) => ({ ...current, [key]: checked })),
              }),
            tab,
          })
        : null}

      {tab === "egress" ? <EgressTab /> : null}
      {tab === "health" ? <PlatformHealthTab enabled /> : null}

      {/* Remounted per opening so a secret typed once is never carried into a
          second attempt. */}
      <ReplaceSecretDialog
        key={`replace-${replaceOpen}`}
        open={replaceOpen}
        onOpenChange={setReplaceOpen}
        label="Teams webhook"
        description="The URL is written to Key Vault first. Only when the vault confirms does the deployment point at the new version."
        isSaving={updateSetting.isPending}
        // A step-up refusal is answered by the re-authentication dialog, not printed here.
        error={isStepUpRequired(updateSetting.error) ? null : updateSetting.error}
        onSubmit={(value) => {
          const stored = () => {
            setReplaceOpen(false);
            notice.success("Teams webhook stored");
          };
          settingsRetry
            .run(
              () => updateSetting.mutateAsync({ key: TEAMS_WEBHOOK_KEY, secretValue: value }),
              stored,
            )
            .then((result) => {
              if (result !== null) stored();
            })
            // The open dialog already prints this failure (`error` above), so it is only
            // logged here; a second copy behind the modal would say it twice (decisions S3).
            .catch((error: unknown) => reportFailureShown(error));
        }}
      />

      <ReauthDialog
        open={settingsRetry.hold.isOpen}
        isRetrying={settingsRetry.hold.isRetrying}
        error={settingsRetry.hold.error}
        onConfirm={settingsRetry.hold.confirm}
        onCancel={settingsRetry.hold.cancel}
        title="Confirm your identity to change platform settings"
        description="Your session is still valid, but changing a platform setting needs a fresh sign-in."
        heldMessage="Your changes are held. If signing in again takes you to your sign-in page, save them again when you are back."
        footnote="Nothing has been saved yet. Cancelling keeps your edits on the screen."
        code={settingsRetry.hold.code}
        blocked={settingsRetry.hold.blocked}
        remember={settingsRetry.hold.remember}
      />
    </div>
  );
}
